import { useEffect, useMemo, useState } from "react";
import { useOrbit } from "../provider";
import type { OrbitProjectSummary, OrbitTaskRow } from "../types";

export interface OrbitTaskBoardProps {
  /** Heading shown above the board. Defaults to the project name. */
  title?: string;
  /** Columns to render, in order. Defaults to a sensible set. */
  columns?: Array<{ id: string; label: string; statuses: string[] }>;
  /** Max tasks per column — extras are collapsed into a "+n more" tail. */
  maxPerColumn?: number;
  /** className applied to the outer wrapper. */
  className?: string;
}

const DEFAULT_COLUMNS: Required<OrbitTaskBoardProps>["columns"] = [
  { id: "backlog", label: "Backlog", statuses: ["backlog", "planned", "open", "pending", "triage"] },
  { id: "todo", label: "Todo", statuses: ["todo", "ready", "queued", "scheduled"] },
  {
    id: "in_progress",
    label: "In Progress",
    statuses: ["in_progress", "doing", "active", "development", "developing", "design", "blocked", "waiting", "waiting_on_client", "started"],
  },
  { id: "in_review", label: "In Review", statuses: ["in_review", "review", "qa", "testing"] },
  { id: "done", label: "Done", statuses: ["done", "complete", "completed", "closed", "deployed"] },
];

const HIDDEN_STATUSES = new Set(["cancelled", "canceled", "duplicate"]);

interface TasksResponse {
  ok: boolean;
  project: OrbitProjectSummary | null;
  tasks: OrbitTaskRow[];
}

const normalizeStatus = (value: string | null): string => (value || "").toLowerCase().replace(/[\s-]+/g, "_");

export function OrbitTaskBoard({
  title,
  columns = DEFAULT_COLUMNS,
  // Was 12 — capped Done at 4 visible rows on HARC because activity feed ate
  // the rest of the slots in In Progress. Raised so the Done column can
  // show the client's full completed history (orbit-bridge returns up to
  // CLIENT_DONE_TASK_LIMIT rows; this is the final per-column display cap).
  maxPerColumn = 500,
  className = "",
}: OrbitTaskBoardProps) {
  const orbit = useOrbit();
  const [data, setData] = useState<TasksResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let hasData = false;

    const fetchBoard = async () => {
      let lastError: Error | null = null;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          return await orbit.call<TasksResponse>("tasks.list");
        } catch (err) {
          lastError = err instanceof Error ? err : new Error("Failed to load");
          await new Promise((resolve) => window.setTimeout(resolve, 350 * (attempt + 1)));
        }
      }
      throw lastError || new Error("Failed to load");
    };

    const loadBoard = async (initialLoad: boolean) => {
      try {
        const res = await fetchBoard();
        if (!cancelled) {
          hasData = true;
          setData(res);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          // Transient poll failures must not wipe a board that already loaded.
          if (!hasData) {
            setError((err as Error).message || "Failed to load");
          }
        }
      } finally {
        if (!cancelled && initialLoad) setLoading(false);
      }
    };

    void loadBoard(true);
    // Tightened 30s → 5s to match the Linear webhook architecture.
    // The webhook (already wired on linear-sync edge fn) upserts
    // project_tasks the moment Linear pushes a change, so widget polls
    // every 5s catch fresh data within ~5s of any Linear edit.
    // Backend gate (LINEAR_SYNC_FRESHNESS_MS) is also 5s so polls hit
    // the cache instead of triggering redundant Linear pulls.
    const interval = window.setInterval(() => {
      void loadBoard(false);
    }, 5_000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [orbit]);

  const grouped = useMemo(() => {
    if (!data?.tasks) return {} as Record<string, OrbitTaskRow[]>;
    const out: Record<string, OrbitTaskRow[]> = {};
    for (const col of columns) out[col.id] = [];
    const fallback = columns[0]?.id;
    for (const t of data.tasks) {
      const status = normalizeStatus(t.status);
      if (HIDDEN_STATUSES.has(status)) continue;
      const col = columns.find((c) => c.statuses.includes(status));
      const key = col?.id || fallback;
      if (!key) continue;
      out[key]!.push(t);
    }
    return out;
  }, [data, columns]);

  if (loading) {
    // Skeleton columns instead of bare "Loading…" — clients should see
    // structure immediately while the first tasks.list call completes.
    return (
      <div className={`orbit-embed orbit-taskboard orbit-taskboard--loading ${className}`} aria-busy="true">
        <header className="orbit-taskboard__header">
          <h3 className="orbit-taskboard__title orbit-taskboard__skeleton-line" aria-hidden>
            &nbsp;
          </h3>
        </header>
        <div className="orbit-taskboard__columns">
          {columns.map((col) => (
            <section key={col.id} className="orbit-taskboard__col">
              <header className="orbit-taskboard__col-header">
                <span className="orbit-taskboard__col-label">{col.label}</span>
                <span className="orbit-taskboard__col-count orbit-taskboard__skeleton-pill" aria-hidden>&nbsp;</span>
              </header>
              <ul className="orbit-taskboard__list">
                {[0, 1, 2].map((i) => (
                  <li key={i} className="orbit-taskboard__card orbit-taskboard__skeleton-card" aria-hidden>
                    <div className="orbit-taskboard__skeleton-line" />
                    <div className="orbit-taskboard__skeleton-line orbit-taskboard__skeleton-line--short" />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
        <span className="orbit-taskboard__loading-label">Loading work…</span>
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className={`orbit-embed orbit-taskboard orbit-taskboard--error ${className}`}>
        <strong>Couldn't load tasks.</strong>
        <div className="orbit-taskboard__error">{error}</div>
        <button
          type="button"
          className="orbit-button orbit-button--secondary"
          onClick={() => {
            setLoading(true);
            setError(null);
            void orbit
              .call<TasksResponse>("tasks.list")
              .then((res) => {
                setData(res);
                setError(null);
              })
              .catch((err) => setError((err as Error).message || "Failed to load"))
              .finally(() => setLoading(false));
          }}
        >
          Try again
        </button>
      </div>
    );
  }
  if (!data?.project) {
    return (
      <div className={`orbit-embed orbit-taskboard orbit-taskboard--empty ${className}`}>
        No active project yet. The team will start one shortly.
      </div>
    );
  }

  return (
    <div className={`orbit-embed orbit-taskboard ${className}`}>
      <header className="orbit-taskboard__header">
        <h3 className="orbit-taskboard__title">{title || data.project.name}</h3>
      </header>

      <div className="orbit-taskboard__columns">
        {columns.map((col) => {
          const rows = grouped[col.id] || [];
          const visible = rows.slice(0, maxPerColumn);
          const extra = rows.length - visible.length;
          const count = rows.length;
          return (
            <section key={col.id} className="orbit-taskboard__col">
              <header className="orbit-taskboard__col-header">
                <span className="orbit-taskboard__col-label">{col.label}</span>
                <span className="orbit-taskboard__col-count">{count}</span>
              </header>
              <ul className="orbit-taskboard__list">
                {visible.map((t) => (
                  <li key={t.id} className={`orbit-taskboard__card orbit-taskboard__card--${col.id}`}>
                    <div className="orbit-taskboard__card-title">{t.title}</div>
                    {t.description ? (
                      <div className="orbit-taskboard__card-description">{t.description}</div>
                    ) : null}
                    <div className="orbit-taskboard__card-meta">
                      {t.priority ? (
                        <span className={`orbit-taskboard__priority orbit-taskboard__priority--${t.priority.toLowerCase()}`}>
                          {t.priority}
                        </span>
                      ) : null}
                      {t.due_date ? (
                        <span className="orbit-taskboard__due">
                          Due {new Date(t.due_date).toLocaleDateString()}
                        </span>
                      ) : null}
                    </div>
                  </li>
                ))}
                {extra > 0 ? (
                  <li className="orbit-taskboard__more">+{extra} more</li>
                ) : null}
                {count === 0 ? (
                  <li className="orbit-taskboard__empty">—</li>
                ) : null}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
}
